/**
 * Attribution cookie helpers
 *
 * One cookie holds first-touch attribution for 30 days. First-touch wins —
 * once set, we don't overwrite (preserves the original acquisition source even
 * if the user later visits via a different channel).
 *
 * On production the cookie is set with Domain=.marketsniper.pro so it's
 * shared across the marketing site (marketsniper.pro) and the app
 * (app.marketsniper.pro). On preview deploys / localhost the domain is
 * omitted so the cookie defaults to the current origin.
 *
 * Cookie format (URL-encoded JSON):
 *   { source, channel, link_id?, survey_source?, survey_channel?, ts }
 */

export const ATTRIBUTION_COOKIE_NAME = "msp_attribution";
export const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

export interface AttributionData {
  source?: string;
  channel?: string;
  link_id?: string;
  survey_source?: string;
  survey_channel?: string;
  ts?: number;
}

/**
 * Decide what cookie domain to use based on the current browser hostname.
 * Returns ".marketsniper.pro" on production so the cookie is shared between
 * subdomains. Returns undefined on previews/localhost so the cookie defaults
 * to the current origin.
 */
function getCookieDomain(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (host === "marketsniper.pro" || host.endsWith(".marketsniper.pro")) {
    return ".marketsniper.pro";
  }
  return undefined;
}

/**
 * Parse a cookie string (e.g. document.cookie or req.headers.get("cookie")).
 * Returns the attribution payload if present and parseable, otherwise null.
 */
export function parseAttributionCookie(cookieHeader: string | null | undefined): AttributionData | null {
  if (!cookieHeader) return null;
  const match = cookieHeader.split(/;\s*/).find((c) => c.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`));
  if (!match) return null;
  const raw = match.slice(ATTRIBUTION_COOKIE_NAME.length + 1);
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded) as AttributionData;
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Read the attribution cookie from the browser.
 * Client-side only. Returns null on the server.
 */
export function readAttributionCookie(): AttributionData | null {
  if (typeof document === "undefined") return null;
  return parseAttributionCookie(document.cookie);
}

/**
 * Serialize attribution data into a Set-Cookie value (without the name=).
 * Used by client-side cookie writes (AttributionTracker, survey).
 */
export function serializeAttributionCookie(data: AttributionData): string {
  const payload = encodeURIComponent(JSON.stringify({ ...data, ts: data.ts ?? Date.now() }));
  let cookie = `${ATTRIBUTION_COOKIE_NAME}=${payload}; Path=/; Max-Age=${ATTRIBUTION_COOKIE_MAX_AGE}; SameSite=Lax`;
  const domain = getCookieDomain();
  if (domain) cookie += `; Domain=${domain}`;
  return cookie;
}

/**
 * Write the attribution cookie from the browser.
 * First-touch wins: if a cookie already exists, do nothing unless `force` is true.
 * Returns true if the cookie was written.
 */
export function writeAttributionCookie(data: AttributionData, force = false): boolean {
  if (typeof document === "undefined") return false;
  if (!force && readAttributionCookie()) return false;
  document.cookie = serializeAttributionCookie(data);
  return true;
}

/**
 * Merge survey answers into the existing cookie (or create a new one with just
 * survey data if no cookie exists). Survey data does NOT overwrite existing
 * UTM source/channel — it only fills in missing fields.
 */
export function mergeSurveyIntoCookie(survey: { survey_source?: string; survey_channel?: string }): void {
  if (typeof document === "undefined") return;
  const existing = readAttributionCookie() ?? {};
  const merged: AttributionData = {
    ...existing,
    survey_source: survey.survey_source || existing.survey_source,
    survey_channel: survey.survey_channel || existing.survey_channel,
    ts: existing.ts ?? Date.now(),
  };
  document.cookie = serializeAttributionCookie(merged);
}

/**
 * Match a referrer hostname on a domain boundary: the domain itself, or a
 * subdomain of it. A substring test is wrong here, and not subtly so —
 * "reddit.com" contains "t.co" and "netflix.com" contains "x.com", so both
 * would be logged as coming from X. Same for forex.com and bitmex.com.
 */
function hostMatches(host: string, domains: string[]): boolean {
  return domains.some((d) => host === d || host.endsWith(`.${d}`));
}

/**
 * Google runs a country domain per market (google.co.uk, google.com.au), so an
 * exact list would miss most of the world. Anchored at a label boundary so
 * "notgoogle.com" doesn't match.
 */
const GOOGLE_HOST = /(^|\.)google\.[a-z]{2,}(\.[a-z]{2,})?$/;

/**
 * Detect attribution from URL params + document.referrer.
 * Returns null if nothing useful was detected (caller should not write a cookie).
 *
 * NOTE: mirrored in market-sniper-website/public/attribution.js, which captures
 * first touch on the marketing site. Change both together or the cookie formats
 * drift and attribution silently goes back to null.
 */
export function detectAttributionFromBrowser(): AttributionData | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  const utmCampaign = params.get("utm_campaign");

  if (utmSource) {
    return {
      source: utmSource,
      channel: utmCampaign || undefined,
      ts: Date.now(),
    };
  }

  const referrer = document.referrer;
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      if (hostMatches(host, ["t.co", "twitter.com", "x.com"])) {
        return { source: "x", ts: Date.now() };
      }
      if (hostMatches(host, ["youtube.com", "youtu.be"])) {
        return { source: "youtube", ts: Date.now() };
      }
      if (GOOGLE_HOST.test(host)) {
        return { source: "google", ts: Date.now() };
      }
      if (hostMatches(host, ["tiktok.com"])) {
        return { source: "tiktok", ts: Date.now() };
      }
      if (hostMatches(host, ["instagram.com"])) {
        return { source: "instagram", ts: Date.now() };
      }
      // Don't write a cookie for unknown referrers — leave it as direct/unknown
      return null;
    } catch {
      return null;
    }
  }

  return null;
}
